// manage-holidays.component.ts
// HR-only view: create / edit / delete company holidays.
// Extracted from HolidaysTabComponent — no isEmployee branching.

import { Component, OnInit, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { ColDef } from 'ag-grid-enterprise';
import { dateFilterParamsLong } from '@rcs/utils';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ErsGridComponent } from '@rcs/grid';
import { LmLoadingOverlayComponent } from '../lm-loading-overlay/lm-loading-overlay.component';
import { DeleteDialogComponent } from '../../dialogs/delete-dialog/delete-dialog.component';
import { Country, HolidayRow, SaveHolidayInput } from '../../models/leave.model';
import { formatDateLong, getDayName, parseLocalDate, toISODate } from '../../utils/date.utils';
import { editDeleteActionRenderer } from '../../utils/grid-cell-renderers';
import { CreateHolidayDialogComponent } from '../../dialogs/create-holiday-dialog/create-holiday-dialog.component';
import { LeaveService } from '../../services/leave.service';
import { TranslocoPipe } from '@jsverse/transloco';

const GRID_CELL_STYLE = { fontSize: '13px', color: '#1e293b' };

@Component({
  selector: 'ers-manage-holidays',
  standalone: true,
  imports: [
    TranslocoPipe,
    CommonModule,
    FormsModule,
    RouterModule,
    MatDialogModule,
    MatFormFieldModule,
    MatSelectModule,
    ErsGridComponent,
    LmLoadingOverlayComponent,
  ],
  templateUrl: './manage-holidays.component.html',
  styleUrls: ['./manage-holidays.component.scss'],
})
export class ManageHolidaysComponent implements OnInit {
  countries: Country[] = [];
  selectedCountryId: string | null = null;
  isLoading = false;
  isSaving = false;

  private nextId = 1;
  private destroyRef = inject(DestroyRef);
  private backendIdMap = new Map<number, string>();
  private allHolidays: HolidayRow[] = [];
  holidays: HolidayRow[] = [];

  colDefs: ColDef[] = [
    {
      headerName: 'Holiday Name',
      field: 'name',
      flex: 2,
      wrapText: true,
      autoHeight: true,
      minWidth: 200,
      filter: 'agSetColumnFilter',
      cellRenderer: (params: any) => {
        const isMandatory = params.data?.optional === 'Mandatory';
        const star = isMandatory
          ? `<span style="color:#FFC107;margin-right:5px;font-size:14px;line-height:1;">★</span>`
          : `<span style="display:inline-block;width:19px;"></span>`;
        return `<span style="display:flex;align-items:center;font-size:13px;color:#1e293b;">${star}${params.value ?? ''}</span>`;
      },
    },
    { headerName: 'Date',             field: 'date',     flex: 1, filter: 'agDateColumnFilter', filterParams: dateFilterParamsLong, cellStyle: GRID_CELL_STYLE },
    { headerName: 'Day',              field: 'day',      flex: 1, filter: 'agSetColumnFilter', cellStyle: GRID_CELL_STYLE },
    { headerName: 'Type',             field: 'type',     flex: 1, filter: 'agSetColumnFilter', cellStyle: GRID_CELL_STYLE },
    { headerName: 'Optional Holiday', field: 'optional', flex: 1, filter: 'agSetColumnFilter', cellStyle: GRID_CELL_STYLE },
    {
      headerName: 'Action',
      field: 'action',
      flex: 0.8,
      minWidth: 90,
      sortable: false,
      cellRenderer: editDeleteActionRenderer,
    },
    // Hidden sort column — default descending sort by modifiedOn date (isoDate)
    { field: 'isoDate', hide: true, sort: 'desc', sortIndex: 0 },
  ];

  gridOptions = {
    suppressCellFocus: true,
    suppressHorizontalScroll: true,
    pagination: true,
    paginationPageSize: 10,
    paginationPageSizeSelector: false,
    rowHeight: 44,
  };

  constructor(private dialog: MatDialog, private leaveService: LeaveService) {}

  ngOnInit(): void {
    forkJoin({ countries: this.leaveService.getCountries() })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ countries }) => { this.countries = countries; });

    this.loadHolidays();
  }

  onCountryChange(): void {
    this.holidays = this.selectedCountryId
      ? this.allHolidays.filter(h => !h.countryId || h.countryId === this.selectedCountryId)
      : [...this.allHolidays];
  }

  onCreateNewHoliday(): void {
    const ref = this.dialog.open(CreateHolidayDialogComponent, {
      width: '640px',
      panelClass: 'lm-dialog-panel',
    });

    ref.afterClosed().subscribe((result: any | null) => {
      if (!result) return;
      const localIso = result.isoDate;
      const saveInput: SaveHolidayInput = {
        holidayName: result.name,
        holidayDate: localIso,
        holidayType: 'Public Holiday',
        isOptional: result.isOptional ?? false,
        applicableToCountryId: result.applicableToCountryId,
        applicableToStateId: result.applicableToStateId,
        notes: result.notes,
      };
      this.isSaving = true;
      this.leaveService.createHoliday(saveInput).subscribe(res => {
        this.isSaving = false;
        if (!res?.holiday) return;
        const newId = this.nextId++;
        this.backendIdMap.set(newId, res.holiday.id);
        const date = parseLocalDate(res.holiday.holidayDate);
        const isoDate = toISODate(date);
        const newRow: HolidayRow = {
          id: newId,
          name: res.holiday.holidayName,
          date: formatDateLong(date),
          isoDate,
          day: getDayName(date),
          type: res.holiday.holidayType,
          optional: res.holiday.isOptional ? 'Optional' : 'Mandatory',
          countryId: res.holiday.applicableToCountryId ?? null,
          stateId: res.holiday.applicableToStateId ?? null,
          notes: res.holiday.notes ?? null,
        };
        this.allHolidays = [newRow, ...this.allHolidays];
        if (!this.selectedCountryId || !newRow.countryId || newRow.countryId === this.selectedCountryId) {
          this.holidays = [newRow, ...this.holidays];
        }
      });
    });
  }

  onUseLastYear(): void {
    // TODO: copy last year holidays
  }

  onCellClick(event: any): void {
    const mouseEvent = event?.event as MouseEvent | undefined;
    if (!mouseEvent) return;
    const target = (mouseEvent.target as HTMLElement)?.closest('[data-action]') as HTMLElement | null;
    if (!target) return;
    const action = target.getAttribute('data-action');
    const id = Number(target.getAttribute('data-id'));
    const holiday = this.holidays.find(h => h.id === id);
    if (!holiday) return;
    if (action === 'delete') this.openDelete(holiday);
    if (action === 'edit')   this.openEdit(holiday);
  }

  private loadHolidays(): void {
    this.isLoading = true;
    this.leaveService.getHolidays().subscribe(backendHolidays => {
      this.nextId = 1;
      this.backendIdMap.clear();
      this.holidays = backendHolidays.map(h => {
        const id = this.nextId++;
        this.backendIdMap.set(id, h.id);
        const date = parseLocalDate(h.holidayDate);
        const isoDate = toISODate(date);
        return {
          id, name: h.holidayName, date: formatDateLong(date), isoDate,
          day: getDayName(date),
          type: h.holidayType,
          optional: h.isOptional ? 'Optional' : 'Mandatory',
          countryId: h.applicableToCountryId ?? null,
          stateId: h.applicableToStateId ?? null,
          notes: h.notes ?? null,
        } as HolidayRow;
      });
      this.holidays.sort((a, b) => {
        const da = parseLocalDate(a.isoDate);
        const db = parseLocalDate(b.isoDate);
        return db.getTime() - da.getTime();
      });
      this.allHolidays = [...this.holidays];
      this.isLoading = false;
    });
  }

  private openEdit(holiday: HolidayRow): void {
    const ref = this.dialog.open(CreateHolidayDialogComponent, {
      width: '640px',
      panelClass: 'lm-dialog-panel',
      data: { holiday },
    });
    ref.afterClosed().subscribe((result: any | null) => {
      if (!result) return;
      const backendId = this.backendIdMap.get(holiday.id);
      const localIso = result.isoDate;
      const saveInput: SaveHolidayInput = {
        ...(backendId ? { id: backendId } : {}),
        holidayName: result.name,
        holidayDate: localIso,
        holidayType: 'Public Holiday',
        isOptional: result.isOptional ?? false,
        applicableToCountryId: result.applicableToCountryId,
        applicableToStateId: result.applicableToStateId,
        notes: result.notes,
      };
      console.log('[ManageHolidays] Update payload:', JSON.stringify(saveInput));
      this.isSaving = true;
      this.leaveService.updateHoliday(saveInput).subscribe({
        next: () => { this.isSaving = false; this.loadHolidays(); },
        error: () => { this.isSaving = false; },
      });
    });
  }

  private openDelete(holiday: HolidayRow): void {
    const ref = this.dialog.open(DeleteDialogComponent, {
      width: '460px',
      panelClass: 'lm-dialog-panel',
      data: { itemName: holiday.name, title: 'Confirm Deletion', message: 'Are you sure you want to delete this holiday?' },
    });
    ref.afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) return;
      const backendId = this.backendIdMap.get(holiday.id);
      if (!backendId) return;
      this.isSaving = true;
      this.leaveService.deleteHoliday(backendId).subscribe(() => {
        this.isSaving = false;
        this.allHolidays = this.allHolidays.filter(h => h.id !== holiday.id);
        this.holidays = this.holidays.filter(h => h.id !== holiday.id);
        this.backendIdMap.delete(holiday.id);
      });
    });
  }
}
